<template>
  <v-card
    v-if="viewData"
    flat
  >
    <v-card-title class="py-0 px-0">
      <v-toolbar
        :height="$vuetify.breakpoint.xsOnly ? 44 : 60"
        class="filter-toolbar"
        flat
      >
        <v-btn
          v-if="userCanAdd"
          :color="$t('Marketing.Tag.ListView.Button.Add.Color')"
          :icon="$vuetify.breakpoint.xsOnly"
          class="elevation-0 ma-1"
          text
          @click="showAddTagDialog"
        >
          <v-icon :left="$vuetify.breakpoint.smAndUp">
            {{ $t('Marketing.Tag.ListView.Button.Add.Icon') }}
          </v-icon>&nbsp;
          <span v-if="$vuetify.breakpoint.smAndUp">{{ $t('Marketing.Tag.ListView.Button.Add.Title') }}</span>
        </v-btn>

        <v-spacer />

        <filter-search v-model="dataTable.search.terms" />
      </v-toolbar>
    </v-card-title>

    <v-container
      class="pa-0"
      fluid
    >
      <v-row>
        <v-col cols="12">
          <v-data-table
            :footer-props="dataTable.footerProps"
            :headers="dataTableHeaders"
            :height="dataTable.resize.bodyHeight"
            :items="dataTable.data"
            :loading="dataTable.loading"
            :loading-text="dataTable.text.loadingText"
            :no-data-text="dataTable.text.noDataText"
            :no-results-text="dataTable.text.noResultsText"
            :options.sync="dataTable.options"
            :search="dataTable.search.terms"
            :server-items-length="dataTable.total"
            fixed-header
            item-key="Id"
          >
            <template #[`item.id`]="{item}">
              {{ item.Id }}
            </template>

            <template #[`item.name`]="{item}">
              <v-chip
                color="indigo"
                label
              >
                {{ item.Name }}
              </v-chip>
            </template>

            <template #[`item.created_at`]="{item}">
              {{ item.CreatedAt }}
            </template>

            <template #[`item.actions`]="{item}">
              <template v-if="userCanDelete">
                <v-tooltip
                  :color="$t('Marketing.Tag.ListView.Button.Edit.Color')"
                  :open-delay="1000"
                  left
                >
                  <template #activator="{on}">
                    <v-btn
                      :class="$t('Marketing.Tag.ListView.Button.Delete.Color')"
                      :small="$vuetify.breakpoint.xsOnly"
                      class="elevation-2 ma-1"
                      dark
                      icon
                      @click="deleteTag(item)"
                      v-on="on"
                    >
                      <v-icon>{{ $t('Marketing.Tag.ListView.Button.Delete.Icon') }}</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('Marketing.Tag.ListView.Button.Delete.Title') }}</span>
                </v-tooltip>
              </template>

              <template v-if="userCanAdd || userCanManage">
                <v-tooltip
                  :color="$t('Marketing.Tag.ListView.Button.Edit.Color')"
                  :open-delay="1000"
                  left
                >
                  <template #activator="{on}">
                    <v-btn
                      :class="$t('Marketing.Tag.ListView.Button.Edit.Color')"
                      :small="$vuetify.breakpoint.xsOnly"
                      class="elevation-2 ma-1"
                      dark
                      icon
                      @click="editTag(item)"
                      v-on="on"
                    >
                      <v-icon>{{ $t('Marketing.Tag.ListView.Button.Edit.Icon') }}</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('Marketing.Tag.ListView.Button.Edit.Title') }}</span>
                </v-tooltip>
              </template>
            </template>

            <template #no-data>
              <v-data-table-no-data
                :button-callback="showAddTagDialog"
                :no-data-obj="$t('Marketing.Tag.ListView.Table.NoData')"
              />
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-container>

    <tag-add-dialog
      :tag="selectedTag"
      :visible.sync="addTagDialog"
      @onSaveResult="selectedTag = null"
    />

    <confirm-dialog
      v-if="selectedTag"
      :html-content="$t('Marketing.Tag.ListView.ConfirmDeleteDialog.Content', {title: selectedTag.Name})"
      :html-title="$t('Marketing.Tag.ListView.ConfirmDeleteDialog.Title')"
      :info-bar="true"
      :info-bar-body="$t('Marketing.Tag.ListView.ConfirmDeleteDialog.Warning')"
      :visible.sync="confirmDialogDeleteVisible"
      :width="560"
      no-button-class="v-btn--outline green--text"
      title-class="red white--text lighten-0"
      yes-button-class="red white--text"
      yes-button-event="on-confirm-delete"
      @on-confirm-delete="onConfirmDeleteTag"
    />
  </v-card>
</template>

<script>
import AppData from '@/mixins/appdata.js'
import Auth from '@/mixins/auth.js'
import DataTable from '@/mixins/data-table/dataTable.js'
import FilterSearch from '@/components/common/filter/FilterSearch.vue'
import SourceType from '@/mixins/orders/sourceType.js'
import {formatDecimal} from '@/lib/currency/currency.js'
import VDataTableNoData from '@/components/common/data-table/VDataTableNoData.vue'
import {clone} from '@/lib/helper/helper.js'
import ConfirmDialog from '@/components/common/ConfirmDialog.vue'
import TagAddDialog from '@/components/marketing/tag/TagAddDialog.vue'

export default {
  Name      : 'Tags',
  components: {
    TagAddDialog,
    ConfirmDialog,
    VDataTableNoData,
    FilterSearch
  },
  directives: {},
  mixins    : [AppData, Auth, DataTable, SourceType],
  data() {
    return {
      confirmDialogDeleteVisible: false,
      addTagDialog              : false,
      selectedTag               : null,

      dataTable: {
        socketCommand: {
          list  : window.SocketCommand.Marketing.Tag.All,
          save  : window.SocketCommand.Marketing.Tag.Save,
          delete: window.SocketCommand.Marketing.Tag.Delete
        },
        options: {
          defaultSortBy: 'id',
          sortBy       : ['id'],
          sortDesc     : [true]
        },
        rejectHeaders: {
          xsOnly   : [],
          smAndDown: [],
          mdAndDown: [],
          lgAndDown: [],
          xlAndDown: []
        },
        headers: [
          {
            text     : this.$t('Marketing.Tag.ListView.Table.Headers.Id'),
            value    : 'id',
            align    : 'right',
            sortable : true,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Marketing.Tag.ListView.Table.Headers.Name'),
            value    : 'name',
            align    : 'left',
            sortable : true,
            cellClass: 'text-caption',
            width    : '100%'
          },
          {
            text     : this.$t('Marketing.Tag.ListView.Table.Headers.CreatedAt'),
            value    : 'created_at',
            align    : 'center',
            sortable : true,
            cellClass: 'text-caption text-no-wrap'
          },
          {
            text     : this.$t('Marketing.Tag.ListView.Table.Headers.Actions'),
            value    : 'actions',
            align    : 'right',
            sortable : false,
            cellClass: 'text-caption px-2',
            width    : 120
          }
        ]
      }
    }
  },
  computed: {},
  watch   : {},
  created() {
  },
  mounted() {
  },
  updated() {
  },
  beforeDestroy() {
  },
  methods: {
    formatDecimal,

    showAddTagDialog() {
      this.addTagDialog = true
    },

    editTag(tag) {
      if (!tag) return
      this.selectedTag = clone(tag)
      this.showAddTagDialog()
    },


    deleteTag(tag) {
      if (!tag) return
      this.selectedTag = tag
      this.confirmDialogDeleteVisible = true
    },
    onConfirmDeleteTag() {
      const tag = this.selectedTag
      window.callAS(window.SocketCommand.Marketing.Tag.Delete, {Id: tag.Id})
      this.selectedTag = null
    }
  }
}
</script>

<style scoped>
:deep(.filter-toolbar .v-toolbar__content) {
  padding: 0 6px;
}

:deep(.v-input--selection-controls.v-input--switch) {
  display: inline-block;
  position: relative !important;
  /*width    : 75px !important;*/
}

:deep(.v-input--selection-controls.v-input--switch label) {
  font-size: 12px !important;
  padding-right: 0;
  margin: 0;
}

:deep(.v-data-table > .v-data-table__wrapper > table:has(tbody tr:first-child.v-data-table__empty-wrapper)) {
  width: 100%;
  height: 100%;
  border-spacing: 0;
}
</style>
